import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useNuxtApp } from 'nuxt/app';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import type { EnterNewPasswordForm } from '~/types/auth/forms/ForgotPasswordForm';
import type { ApiInstance } from '~/types/plugins/api';
import { EnterNewPassword as EnterNewPasswordContent } from '~/utils/contents/auth/forms/ForgotPasswordForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useSendForgotPasswordCode() {
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<EnterNewPasswordForm>(getInitialState());

  const toast = useToast();

  const validations = z
    .object({
      password: z
        .string()
        .min(8, {
          message: `${EnterNewPasswordContent.fields.password.label} must be at least 8 characters.`,
        })
        .regex(/[A-Z]/, {
          message: `${EnterNewPasswordContent.fields.password.label} must contain at least one uppercase letter.`,
        })
        .regex(/[a-z]/, {
          message: `${EnterNewPasswordContent.fields.password.label} must contain at least one lowercase letter.`,
        })
        .regex(/[0-9]/, {
          message: `${EnterNewPasswordContent.fields.password.label} must contain at least one number.`,
        })
        .regex(/[^A-Za-z0-9]/, {
          message: `${EnterNewPasswordContent.fields.password.label} must contain at least one special character.`,
        }),
      password_confirmation: z.string().min(1, {
        message: `${EnterNewPasswordContent.fields.password_confirmation.label} is required.`,
      }),
    })
    .partial()
    .refine((data) => data.password === data.password_confirmation, {
      message: "Passwords don't match. Please try again.",
      path: ['password_confirmation'],
    });
  const resolver = zodResolver(validations);

  const submit = async (email: string, token: string): Promise<boolean> => {
    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };

      const result = await $apiClient.post<{
        error?: {
          data?: {
            errors?: Record<string, string[]>;
          };
        };
      }>('auth/reset-password', { username: email, token, ...form.value });

      if (result.error) {
        errors.value = result.error.data?.errors || {};
        return false;
      } else {
        reset();
        return true;
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): EnterNewPasswordForm {
    return {
      password: '',
      password_confirmation: '',
    };
  }

  return {
    loading,
    form,
    errors,
    validations,
    resolver,

    submit,
    reset,
  };
}
